// Helpers

import { merge } from "lodash";
import { local_schema, ILocalState$, IRemoteState$, ICreateStateProps, IObservers, IActions } from "./types";
import { api, IAPI, IContentExtract, IMeasure } from "@/core/shared/api";
import { transforms } from "@/core/shared/transforms";
import { z } from "zod";
import { legend } from "@/core/utils/legend-state/core";
import { rc } from "../../rc";
import { auth } from "@/core/state/auth";
import { metadata } from "@/core/state/metadata";
import { report_builder } from "@/features/report-builder/state";
import { ITransformedContentExtract } from "../../../extracts/instance/state/types";
import { IPeersetCompany } from "@/features/report-builder/state/types";
import dayjs from "dayjs";

// Config
const config: z.infer<typeof local_schema>["config"] = {
  company: {
    measure_filter_to_measure_name_map: {
      EPS_PER_COMPANY: {
        default: "EPS_PER_COMPANY",
      },
      CEO_APPROVAL_PER_COMPANY: {
        default: "CEO_APPROVAL_PER_COMPANY",
      },
      RECOMMEND_TO_FRIEND_PER_COMPANY: {
        default: "RECOMMEND_TO_FRIEND_PER_COMPANY",
      },
      COUNT_PER_COMPANY: {
        default: "COUNT_PER_COMPANY",
      },
    },
    filters: {
      hide: false,
    },
  },
  measure: {
    hide: false,
    default: "NSS",
    measures: [
      {
        id: "EPS_PER_COMPANY",
        label: "Net Rating",
        unit: "%",
        showSign: true,
        domain: [-100, 100],
        filter: {
          checked: true,
          disabled: false,
        },
      },
      {
        id: "CEO_APPROVAL_PER_COMPANY",
        label: "Ceo Approval ",
        unit: "%",
        domain: [0, 5],
        filter: {
          checked: true,
          disabled: false,
        },
      },
      {
        id: "RECOMMEND_TO_FRIEND_PER_COMPANY",
        label: "Recommend to a Friend ",
        unit: "%",
        domain: [0, 5],
        filter: {
          checked: true,
          disabled: false,
        },
      },
      {
        id: "COUNT_PER_COMPANY",
        label: "Employee Review Count",
        unit: "",
        domain: undefined,
        compactYAxisValues: true,
        filter: {
          checked: true,
          disabled: false,
        },
      },
    ],
  },
  visualisation: [
    {
      id: "table",
      label: "Table",
    },
  ],
};

export function createState(
  instance_id: string,
  props: ICreateStateProps,
): { local: ILocalState$; remote: IRemoteState$ } {
  const local = helpers.validateAndMergeState(
    {
      label: "Employee Voice: Company Personality",
      ui_merged: false,
      config,
      views: {
        currentView: "analysis",
        analysis: {
          chart_config: {
            entities: {},
            polar_axis: {
              data_key: "company_id",
              label: "Companies",
              domain: config.measure.measures.find((m) => m.id === "NSS")?.domain,
            },
          },
          selectors: {
            visualisation: {
              selected: "table",
            },
          },
          filters: {
            company: legend.features.filter.createObservableState(),
            measures: legend.features.filter.createObservableState(),
            applied_filters: {
              company_id: [],
              measures: [
                // "EPS_PER_COMPANY",
                "CEO_APPROVAL_PER_COMPANY",
                "RECOMMEND_TO_FRIEND_PER_COMPANY",
                "COUNT_PER_COMPANY",
              ],
            },
          },
        },
      },
    },
    props.local,
  );

  const remote: IRemoteState$ = {
    measures: {
      pending: false,
      error: false,
      data: legend.sync.synced({
        initial: [],
        get: async () => {
          const instance = rc.registry.actions.getInstance(instance_id);
          try {
            instance.remote.measures.pending.set(true);

            const peerset = report_builder.remote.peerset.data.get();

            const company_config = instance.local.config.company.get();

            const applied_company_id = instance.local.views.analysis.filters.applied_filters.company_id.get();
            const applied_measures = instance.local.views.analysis.filters.applied_filters.measures.get();

            const applied_measure_names = applied_measures?.map((applied_measure) => {
              const measure_name = company_config.measure_filter_to_measure_name_map[applied_measure].default;
              return measure_name;
            });

            const params: Parameters<IAPI["deltabase_data_api"]["measures"]["get"]>["0"] = {
              company_id: applied_company_id,
              measure_name: applied_measure_names,
              sort_field: "year",
              sort_direction: "ASC",
            };

            // Guard against no company selected - Data API will return an error 400 otherwise
            if (
              params.company_id?.length === 0 ||
              params.measure_name?.length === 0 ||
              params.measure_name === undefined
            ) {
              return [];
            }

            const promises = [api.deltabase_data_api.measures.get(params)];

            const [measures_response] = await Promise.all(promises);

            const company = Object.keys(peerset);
            for (const companyId of company) {
              // handle per company data
              const applied_company = applied_company_id?.find((c) => c === companyId);

              for (const measure_name of params.measure_name) {
                const found_measure = measures_response.data.find(
                  (m) => m.company_id === companyId && m.measure_name === measure_name,
                );

                if (!found_measure && applied_company) {
                  measures_response.data.push({
                    measure_signal: "NO_SIGNAL",
                    measure_id: "",
                    measure_name,
                    measure_range: "",
                    measure_unit: "",
                    measure_value: "",
                    brief_id: "",
                    city: "",
                    company_id: companyId,
                    continent: "",
                    cost: NaN,
                    country: "",
                    date: "",
                    function: "",
                    job_id: "",
                    leaf_level: false,
                    month: "",
                    rating: "",
                    sector: "",
                    seniority: "",
                    skill: "",
                    source_id: "",
                    source_url: "",
                    state: "",
                    topic_name: "",
                    user_name: "",
                    year: NaN,
                    years_in_current_company: "",
                  });
                }
              }
            }
            // .

            // group by topic
            const chart_data = helpers.formatChartData({
              data: measures_response.data,
              company_config,
            });

            const table_data = helpers.formatTableData({
              data: measures_response.data,
              company_config,
              peerset,
            });

            legend.state.batch(() => {
              instance.remote.measures.pending.set(false);
              instance.remote.measures.error.set(false);
            });

            return { chart_data, table_data };
          } catch (error) {
            legend.state.batch(() => {
              instance.remote.measures.pending.set(false);
              instance.remote.measures.error.set(true);
            });
          }
        },
        waitFor: () => {
          const token = auth.state$.token.get();
          return !!token;
        },
      }),
    },
  };

  return { local, remote };
}

export const actions: IActions = {
  views: {
    analysis: {
      selectors: {
        visualisation: {
          set: (instance_id: string, visualisation: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            instance.local.views.analysis.selectors.visualisation.selected.set(visualisation);
          },
        },
      },
      filters: {
        company: {
          applyAll: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const applied: string[] = [];
            const filters = instance.local.views.analysis.filters.company.data.get();
            Object.values(filters).forEach((filter) => {
              if (filter.checked) {
                applied.push(filter.id);
              }
            });
            instance.local.views.analysis.filters.applied_filters.company_id.set(applied);
          },
          toggle: (instance_id: string, filter_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggle({
              state: instance.local.views.analysis.filters.company,
              params: { id: filter_id },
            });
          },
          toggleGroup: (instance_id: string, filter_group: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggleGroup({
              state: instance.local.views.analysis.filters.company,
              params: { group: filter_group },
            });
          },
          getFiltersByGroup: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const out = legend.features.filter.getFiltersByGroup({
              state: instance.local.views.analysis.filters.company,
            });
            return out;
          },
        },
        measures: {
          applyAll: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const applied: string[] = [];
            const filters = instance.local.views.analysis.filters.measures.data.get();
            Object.values(filters).forEach((filter) => {
              if (filter.checked) {
                applied.push(filter.id);
              }
            });
            instance.local.views.analysis.filters.applied_filters.measures.set(applied);
          },
          toggle: (instance_id: string, filter_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggle({
              state: instance.local.views.analysis.filters.measures,
              params: { id: filter_id },
            });
          },
          toggleGroup: (instance_id: string, filter_group: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggleGroup({
              state: instance.local.views.analysis.filters.measures,
              params: { group: filter_group },
            });
          },
          getFiltersByGroup: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const out = legend.features.filter.getFiltersByGroup({
              state: instance.local.views.analysis.filters.measures,
            });
            return out;
          },
        },
      },
    },
  },
};

export const observers: IObservers = {
  view: {
    analysis: {
      chart_config: {
        sync_to_state: (instance_id: string) => {
          if (!instance_id) return;
          legend.state.observe(() => {
            const instance = rc.registry.actions.getInstance(instance_id);

            const peerset = report_builder.remote.peerset.data.get();

            // sync chart config
            const applied_company_id = instance.local.views.analysis.filters.applied_filters.company_id.get() ?? [];

            const config: z.infer<typeof local_schema>["views"]["analysis"]["chart_config"]["entities"] = {};

            // add company entities to chart config
            applied_company_id.forEach((company_id, idx) => {
              const company = peerset[company_id];
              if (!company) return;
              config[company_id] = {
                id: company_id,
                label: company?.company_name ?? "",
                color: `hsl(var(--chart-deltabase-company-${idx + 1}))`,
              };
            });

            instance.local.views.analysis.chart_config.entities.set(config);
          });
        },
      },
      filters: {
        company: {
          init: (instance_id: string) => {
            if (!instance_id) return;
            legend.state.observe<{ peerset_key: string }>((e) => {
              const instance = rc.registry.actions.getInstance(instance_id);
              const peerset = report_builder.remote.peerset.data.get();
              const current_peerset_key = JSON.stringify(Object.keys(peerset));

              if (!peerset || current_peerset_key.length === 0 || current_peerset_key === e.previous?.peerset_key) {
                return;
              }

              const companies = Object.values(peerset);

              // populate filter state
              legend.state.batch(() => {
                legend.features.filter.batchAddFilter({
                  state: instance.local.views.analysis.filters.company,
                  params: {
                    data: companies.map((company, idx) => ({
                      id: company.company_id,
                      label: company.company_name,
                      checked: true,
                      disabled: false,
                      group: "",
                    })),
                  },
                });
                actions.views.analysis.filters.company.applyAll(instance_id);
              });

              const peerset_key = JSON.stringify(Object.keys(peerset));

              return { peerset_key };
            });
          },
        },
        measures: {
          init: (instance_id: string) => {
            if (!instance_id) return;
            legend.state.observe<{ key: string }>((e) => {
              const instance = rc.registry.actions.getInstance(instance_id);
              const measures = instance.local.config.measure.measures.get();
              const current_keys = JSON.stringify(measures.map((m) => m.id));

              if (!measures || current_keys.length === 0 || current_keys === e.previous?.key) {
                return;
              }

              // populate filter state
              legend.state.batch(() => {
                legend.features.filter.batchAddFilter({
                  state: instance.local.views.analysis.filters.measures,
                  params: {
                    data: measures.map((measure, idx) => ({
                      id: measure.id,
                      label: measure.unit ? `${measure.label} (${measure.unit})` : measure.label,
                      checked: true,
                      disabled: false,
                      group: "",
                    })),
                  },
                });
                actions.views.analysis.filters.measures.applyAll(instance_id);
              });

              const key = JSON.stringify(Object.keys(current_keys));

              return { key };
            });
          },
        },
      },
    },
  },
};

const helpers = {
  validateAndMergeState: (state: ILocalState$, new_state?: object) => {
    if (new_state !== undefined) {
      const isValid = local_schema.safeParse(new_state);
      if (!isValid.success) {
        throw new Error(
          `Invalid props provided to createState$. Validation errors: ${JSON.stringify(isValid.error.format(), null, 2)}`,
        );
      }
      state = merge(state, new_state);
      return state;
    }
    return state;
  },

  transformMeasureScore: (measure: IMeasure, measure_type: string) => {
    if (measure_type === "EPS_PER_COMPANY") {
      const score = transforms.eps.toPercentage(measure.measure_value);
      return score;
    } else if (measure_type === "CEO_APPROVAL_PER_COMPANY") {
      const score = transforms.score.toPercentage(measure.measure_value);
      return score;
    } else if (measure_type === "RECOMMEND_TO_FRIEND_PER_COMPANY") {
      const score = transforms.score.toPercentage(measure.measure_value);
      return score;
    } else if (measure_type === "COUNT_PER_COMPANY") {
      const score = transforms.count.toNumber(measure.measure_value);
      return score;
    }

    return null;
  },

  mapMeasureNameToParams(
    measureMap: Record<string, { default: string; withGeoContinent: string; withGeoCountry: string }>,
    selected_measure_id: string,
    selected_location_id: string | undefined,
    selected_location_type: string | undefined,
  ) {
    if (selected_location_id && selected_location_type) {
      if (selected_location_type === "continent") {
        const measure_name = measureMap[selected_measure_id];
        return measure_name.withGeoContinent;
      } else if (selected_location_type === "country") {
        const measure_name = measureMap[selected_measure_id];
        return measure_name.withGeoCountry;
      }
    }
    const measure_name = measureMap[selected_measure_id];
    return measure_name.default;
  },

  formatChartData: (props: { data: IMeasure[]; company_config: z.infer<typeof local_schema>["config"]["company"] }) => {
    const { data, company_config } = props;

    // group by topic
    const grouped_data: Record<string, IMeasure[]> = {};
    data.forEach((measure) => {
      const group = measure.company_id;
      if (!grouped_data[group]) {
        grouped_data[group] = [];
      }
      grouped_data[group].push(measure);
    });

    type ChartRecord = Record<string, number | string | null>;
    const chart_data: ChartRecord[] = [];

    Object.entries(grouped_data).forEach(([company_id, measures]) => {
      const entry: ChartRecord = { company_id };
      measures.forEach((measure) => {
        const measure_name = company_config.measure_filter_to_measure_name_map[measure.measure_name].default;
        const score =
          measure.measure_signal === "NO_SIGNAL" ? null : helpers.transformMeasureScore(measure, measure_name);

        entry[measure.company_id] = score;
      });
      chart_data.push(entry);
    });

    // Check if all values across all records are null
    const allValuesNull = chart_data.every((record) => {
      // Get all values except 'year' field
      const values = Object.entries(record)
        .filter(([key]) => key !== "topic")
        .map(([_, value]) => value);
      return values.every((value) => value === null);
    });

    if (allValuesNull) {
      return [];
    }

    return chart_data;
  },
  formatTableData: (props: {
    data: IMeasure[];
    company_config: z.infer<typeof local_schema>["config"]["company"];
    peerset: Record<string, IPeersetCompany>;
  }) => {
    const { data, company_config, peerset } = props;

    // group by company
    const grouped_data: Record<
      string,
      {
        label: string;
        measures: IMeasure[];
      }
    > = {};
    data.forEach((measure) => {
      const company = peerset[measure.company_id];
      if (!grouped_data[measure.company_id]) {
        grouped_data[measure.company_id] = {
          label: company?.company_name,
          measures: [],
        };
      }
      grouped_data[measure.company_id].measures.push(measure);
    });

    type TableRecord = Record<
      string,
      {
        id: string;
        score: number | string | null;
        signal?: string;
        label: string;
      }
    >;
    const company_table_data: TableRecord[] = [];

    Object.entries(grouped_data).forEach(([entity_id, { label, measures }]) => {
      const entry: TableRecord = {
        company_id: {
          id: entity_id,
          score: entity_id,
          label: label,
        },
      };
      measures.forEach((measure) => {
        const measure_name = company_config.measure_filter_to_measure_name_map[measure.measure_name].default;
        const score =
          measure.measure_signal === "NO_SIGNAL" ? null : helpers.transformMeasureScore(measure, measure_name);

        entry[measure.measure_name] = {
          id: measure.measure_name,
          score: score,
          label: measure.measure_name,
          signal: measure.measure_signal,
        };
      });

      company_table_data.push(entry);
    });

    // Check if all values across all records are null
    const allValuesNull = company_table_data.every((record) => {
      // Get all values except 'year' field
      const values = Object.entries(record)
        .filter(([key]) => key !== "topic")
        .map(([_, value]) => value);
      return values.every((value) => value === null);
    });

    if (allValuesNull) {
      return [];
    }

    return company_table_data;
  },
};
